import * as React from 'react'
import { PropsWithChildren } from 'react'
import { ActionFilters } from '../types/filter-types'
import { attempt } from '../async/utils'
import { fetchFilters } from '../async/sanity'
import { SanityFilter } from '../utilities/sanity-filters'

export type Filters = {
  allFilters?: ActionFilters
  filtersError?: string
  filtersLoading?: boolean
  fetchFiltersIfNotFetched: Function
  fetchAllFiltersIfNotFetched: Function
}

export const FiltersContext = React.createContext<Filters>({
  fetchFiltersIfNotFetched: () => {},
  fetchAllFiltersIfNotFetched: () => {}
})

export const FiltersProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [allFilters, setFilters] = React.useState<ActionFilters>()
  const [filtersError, setFiltersError] = React.useState<string>()
  const [filtersLoading, setFiltersLoading] = React.useState(false)
  // Filters already loaded, and ones with a request on the way. Several
  // components ask for filters as they mount, all before state updates, so
  // tracking these in refs keeps each filter to a single request.
  const loadedFilters = React.useRef(new Set<string>())
  const inFlightFilters = React.useRef(new Set<SanityFilter>())

  const getFilters = React.useCallback(async (requested?: SanityFilter[]) => {
    const toFetch = (
      requested && requested.length > 0 ? requested : Object.values(SanityFilter)
    ).filter(filter => !loadedFilters.current.has(filter) && !inFlightFilters.current.has(filter))

    if (toFetch.length === 0) {
      return
    }

    toFetch.forEach(filter => inFlightFilters.current.add(filter))
    setFiltersLoading(true)

    await attempt(
      async () => {
        await fetchFilters(
          {},
          filters => {
            Object.keys(filters).forEach(filter => loadedFilters.current.add(filter))
            setFilters(prev => ({ ...prev, ...filters }))
          },
          toFetch
        )
      },
      err => setFiltersError(err || 'Failed to fetch filters')
    )

    toFetch.forEach(filter => inFlightFilters.current.delete(filter))
    setFiltersLoading(inFlightFilters.current.size > 0)
  }, [])

  const fetchAllFiltersIfNotFetched = React.useCallback(() => {
    getFilters()
  }, [getFilters])

  const fetchFiltersIfNotFetched = React.useCallback(
    (filters: SanityFilter[]) => {
      getFilters(filters)
    },
    [getFilters]
  )
  const value = React.useMemo(
    () => ({
      allFilters,
      filtersLoading,
      filtersError,
      fetchFiltersIfNotFetched,
      fetchAllFiltersIfNotFetched
    }),
    [allFilters, filtersLoading, filtersError]
  )

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
}

export const filtersState = () => React.useContext(FiltersContext)
